import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

type TaxCodeEntry = {
  name: string
  format: RegExp
}

type FullTaxCodeEntry = TaxCodeEntry & {
  label: string
  button: string
  error: string
}

type TaxCodeData = Record<string, Record<string, TaxCodeEntry>>

type TaxCodeProps = Partial<FullTaxCodeEntry> & {
  getValue: (c?: string, s?: string) => Partial<FullTaxCodeEntry>
  data: TaxCodeData
}

const useTaxCode = (country?: string, state?: string): TaxCodeProps => {
  const { t } = useTranslation()
  const data: TaxCodeData = useMemo(
    () => ({
      ca: {
        qc: {
          name: t('r.payment.edit.make_payment.qc_tax_code.label', 'QST'),
          format: /^(\d{10}([tT][qQ]\d{4}))$/
        }
      }
    }),
    [t]
  )
  const getValue = (country?: string, state?: string): TaxCodeEntry | void => {
    const loc = country?.toLowerCase() || ''
    const prov = state?.toLowerCase() || ''
    return data?.[loc]?.[prov]
  }
  const getButtonText = (data: TaxCodeEntry): string => {
    const value = data.name
    return t('r.payment.edit.make_payment.tax_code.button', {
      defaultValue: 'Do you have a {{value}} number?',
      value
    })
  }
  const getErrorText = (data: TaxCodeEntry): string => {
    const value = data.name
    return t('r.payment.edit.make_payment.tax_code.error', {
      defaultValue: 'We could not validate your {{value}} number.',
      value
    })
  }
  const getLabelText = (data: TaxCodeEntry): string => {
    const value = data.name
    return t('r.payment.edit.make_payment.tax_code.label', {
      defaultValue: '{{value}} #',
      value
    })
  }
  const getFullValue = (
    country?: string,
    state?: string
  ): Partial<FullTaxCodeEntry> => {
    const value = getValue(country, state)
    if (!value) return {}
    return {
      button: getButtonText(value),
      error: getErrorText(value),
      label: getLabelText(value),
      ...value
    }
  }
  const values = getFullValue(country, state)
  return { data, getValue: getFullValue, ...values }
}

export default useTaxCode
